import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type AiModelConfig } from '../api/aimodels/useAiModels';
import {
  createModelSelectionSlice,
  getSelectionForResource,
  type ModelSelectionEntry,
  type ModelSelectionSlice,
} from './slices/createModelSelectionSlice';

export { getSelectionForResource } from './slices/createModelSelectionSlice';

export type ModelSelectionState = ModelSelectionSlice;

export const MAX_SELECTIONS = 50;

export const pruneSelections = (
  selections: Record<string, ModelSelectionEntry>,
): Record<string, ModelSelectionEntry> => {
  const all = Object.entries(selections);

  if (all.length <= MAX_SELECTIONS) {
    return selections;
  }

  return Object.fromEntries(all.sort(([, a], [, b]) => b.lastAccessed - a.lastAccessed).slice(0, MAX_SELECTIONS));
};

/**
 * Imperative helper — reads the model selection for a given resource from the store.
 * Use in callbacks / non-React code instead of reaching into `.getState().selections`.
 */
export const getModelSelection = (resourceId: string) =>
  getSelectionForResource(useModelSelectionStore.getState().selections, resourceId);

/**
 * Imperative helper — migrates the persisted model selection from one
 * resource to another. Use in callbacks / non-React code instead of
 * reaching into `.getState().migrateSelection(...)`.
 */
export const migrateModelSelection = (fromResourceId: string, toResourceId: string) =>
  useModelSelectionStore.getState().migrateSelection(fromResourceId, toResourceId);

export const useModelSelectionStore = create<ModelSelectionState>()(
  persist(
    (...args) => ({
      ...createModelSelectionSlice(...args),
    }),
    {
      migrate: () => {
        return { selections: {} };
      },
      name: 'zustand/ag-ui-chat/model-selection',
      onRehydrateStorage: () => (state) => {
        if (!state) {
          return;
        }
        state.selections = pruneSelections(state.selections);
      },
      partialize: (state) => ({
        selections: state.selections,
      }),
      version: 2,
    },
  ),
);

// Derived hooks
export const useSelectedModel = (resourceId: string) => {
  return useModelSelectionStore((state) => getSelectionForResource(state.selections, resourceId).selectedModel);
};

export const useIsAutoModel = (resourceId: string) => {
  return useModelSelectionStore((state) => getSelectionForResource(state.selections, resourceId).isAuto);
};

export const useSetSelectedModel = () => {
  return useModelSelectionStore((state) => state.setSelectedModel);
};

export const useSetIsAutoModel = () => {
  return useModelSelectionStore((state) => state.setIsAuto);
};

/**
 * Validates the persisted model selection against the currently available models.
 * If the stored model is no longer available, resets to the first available model and forces auto mode.
 * If no model is selected yet, sets it to the first available model.
 */
export const validateModelSelection = (resourceId: string, availableModels: AiModelConfig[]) => {
  const { setSelectedModel, setIsAuto } = useModelSelectionStore.getState();
  const { selectedModel } = getModelSelection(resourceId);
  const fallbackModel = availableModels[0];

  if (!fallbackModel) {
    return;
  }

  if (!selectedModel) {
    setSelectedModel(resourceId, fallbackModel);
    return;
  }

  const freshModel = availableModels.find((m) => m.name === selectedModel.name);

  if (freshModel) {
    setSelectedModel(resourceId, freshModel);
  } else {
    setSelectedModel(resourceId, fallbackModel);
    setIsAuto(resourceId, true);
  }
};
